import React, { memo, useCallback, useRef } from "react";

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";

import { PinturaEditor } from "@pqina/react-pintura";
import {
  getEditorDefaults,
  createDefaultImageReader,
  PinturaDefaultImageWriterResult,
  createMarkupEditorToolbar,
} from "@pqina/pintura";
import "@pqina/pintura/pintura.css";
import { IFileInTransit } from "modules/providers/index-db-provider/index-db-models";
import { useFile } from "modules/providers/file-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import colors from "app/colors";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { useIndexDb } from "modules/providers/index-db-provider";

interface IProps {
  fileInTransit: IFileInTransit;
}

const EditImageModal = ({ fileInTransit }: IProps) => {
  const { db } = useIndexDb();
  const { closeModal, closeDrawers } = useOverlay();
  const { replaceImage } = useFile();
  const editor = useRef<PinturaEditor>(null);

  const onProcessClicked = useCallback(
    async (detail: PinturaDefaultImageWriterResult) => {
      await db.file.put(fileInTransit);
      replaceImage(fileInTransit, detail.dest);
      closeModal();
      closeDrawers({ types: [DRAWER_TYPE.MESSAGE_THREAD_FILES] }); //in case it's open
    },
    [db.file, fileInTransit, replaceImage, closeModal, closeDrawers],
  );

  return (
    <Modal isOpen onClose={closeModal} size="full" isCentered>
      <ModalOverlay />
      <ModalContent h="full" w="full">
        <ModalBody bg={colors.black}>
          <ModalCloseButton color={colors.white} />
          <VStack w="full" h="full">
            <Text fontSize={18} fontWeight={700} color={colors.white}>
              Edit Image
            </Text>
            <PinturaEditor
              ref={editor}
              {...getEditorDefaults()}
              onProcess={onProcessClicked}
              util="annotate"
              utils={["annotate", "crop", "resize"]}
              layoutDirectionPreference="vertical"
              src={fileInTransit.url}
              imageReader={createDefaultImageReader({
                request: {
                  credentials: "same-origin",
                },
              })}
              markupEditorToolbar={createMarkupEditorToolbar([
                "sharpie",
                "eraser",
                "line",
                "arrow",
                "rectangle",
                "ellipse",
                "text",
              ])}
            />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(EditImageModal);
